import { useMock } from '../../API/constants';
import {
  USER_TYPES,
  ORIGIN_TYPES,
  USER_TYPE_STRING,
  CUSTOMER_SERVICE_NUMBER,
  PRODUCER_SERVICE_NUMBER,
} from '../../constants/common';
import {
  CUSTOMER_SERVICE_NUMBER_CONFIGURATION,
  MEMBER_DATA_SOURCE,
  MEMBER_DERIVED_LOB,
  MEMBER_STATE_CODE,
} from '../../constants/memberConstants';
import { base64DecodeJwtPayload } from '../../helpers/cryptoHelper';
import { getStateCodeFromParsedUrl } from '../../helpers/navigationHelper';
import { useAppContext } from '../../store';

const useUserType = () => {
  const {
    memberDetails,
    setMemberDetails,
    accessTokenData,
    userDetails,
    limitFunctionality,
    binderPayNowLoginDetails,
    applicantBinderDetails,
  } = useAppContext();
  const { userType, accessTokenValid, memberBillingInfoRequestPayload } =
    memberDetails;
  const { state, dataSource, derivedLob } =
    memberBillingInfoRequestPayload ?? {};
  const { origin } = userDetails;
  const isBAMOrPublicSiteActiveUser =
    userType === USER_TYPES.BAM_PUBLICSITE_ACTIVE_MEMBER;
  const isCA = userType === USER_TYPES.CUSTOMER_ADVOCATE;
  const isCAGovtMember = userType === USER_TYPES.CA_GOVT_USER;
  const isCATermedMember = userType === USER_TYPES.CA_TERMED_MEMBER;
  const isOneTimePaymentActiveUser =
    userType === USER_TYPES.ONE_TIME_PAYMENT_ACTIVE_MEMBER;
  const isActiveUser =
    (userType === USER_TYPES.ONE_TIME_PAYMENT_ACTIVE_MEMBER &&
      !limitFunctionality) ||
    userType === USER_TYPES.BAM_PUBLICSITE_ACTIVE_MEMBER ||
    userType === USER_TYPES.NMA_IFM_USER;
  const isBAMUser =
    userType === USER_TYPES.BAM_PUBLICSITE_ACTIVE_MEMBER &&
    origin === ORIGIN_TYPES.BAM;
  const isBAMActiveGovernmentUser =
    userType === USER_TYPES.BAM_GOVERNMENT_ACTIVE_MEMBER &&
    origin === ORIGIN_TYPES.BAM_GOVERNMENT_ACTIVE;
  const isOneTimeGovernmentActiveUser =
    userType === USER_TYPES.ONE_TIME_GOVERNMENT_ACTIVE_MEMBER &&
    origin === ORIGIN_TYPES.ONE_TIME_GOVERNMENT_ACTIVE;
  const isMobileGovermentActiveUser =
    userType === USER_TYPES.NMA_GOVT_USER &&
    (origin === ORIGIN_TYPES.NMA || origin === ORIGIN_TYPES.UMA);
  const isActiveGovernmentUser =
    isOneTimeGovernmentActiveUser ||
    isBAMActiveGovernmentUser ||
    isMobileGovermentActiveUser ||
    isCAGovtMember;
  const isGuestGovernmentUser =
    userType === USER_TYPES.GOVERNMENT_GUEST &&
    origin === ORIGIN_TYPES.GUEST_GOVERNMENT;
  const isGovernmentUser = isActiveGovernmentUser || isGuestGovernmentUser;
  const isGuestUser = userType === USER_TYPES.GUEST;
  const isGovernmentUser_WithMedsup =
    isGovernmentUser &&
    derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_MEDICARE_SUPPLEMENTAL;
  const isGovernmentUser_WithPDP =
    isGovernmentUser && derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_PDP;
  const isGovernmentUser_WithMAPD =
    isGovernmentUser && derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_MEDICARE;
  const isActiveGovernmentTMGMedsupp =
    isActiveGovernmentUser &&
    dataSource === MEMBER_DATA_SOURCE.TMG &&
    derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_MEDICARE_SUPPLEMENTAL;
  const isActiveGovernmentMAPDOrPDP =
    isActiveGovernmentUser &&
    (derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_PDP ||
      derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_MEDICARE);
  const isActiveGovernmentTMGMember =
    isActiveGovernmentMAPDOrPDP || isActiveGovernmentTMGMedsupp;
  const isActiveGovernmentMedsuppLegacy =
    isActiveGovernmentUser &&
    derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_MEDICARE_SUPPLEMENTAL &&
    dataSource === MEMBER_DATA_SOURCE.VMS;
  const isMobileIFMActiveUser =
    userType === USER_TYPES.NMA_IFM_USER &&
    (origin === ORIGIN_TYPES.NMA || origin === ORIGIN_TYPES.UMA);
  const isMobileBAMUser = isMobileGovermentActiveUser || isMobileIFMActiveUser;
  const isBrokerUser =
    userType === USER_TYPES.BROKER && origin === ORIGIN_TYPES.BROKER;

  const setUserType = (type: USER_TYPES) => {
    setMemberDetails({ ...memberDetails, userType: type });
  };

  const accessTokenDecoded = base64DecodeJwtPayload(
    accessTokenData.access_token,
  );

  const getUserTypeStringForDerivedLob = (USER_TYPE_STRING: string) => {
    return `${USER_TYPE_STRING}${derivedLob ? '_' + derivedLob : ''}`;
  };

  const getUserTypeString = () => {
    if (isBAMOrPublicSiteActiveUser)
      return USER_TYPE_STRING.BAM_PUBLICSITE_ACTIVE_MEMBER;
    else if (isCA && !isCaBinderPayLaterUser)
      return USER_TYPE_STRING.CUSTOMER_ADVOCATE;
    else if (isOneTimePaymentActiveUser) return USER_TYPE_STRING.ACTIVE_MEMBER;
    else if (isGuestUser) return USER_TYPE_STRING.GUEST;
    else if (isBinderPayLaterUser) return USER_TYPE_STRING.BINDER_PAY_LATER;
    else if (isCaBinderPayLaterUser)
      return USER_TYPE_STRING.CA_BINDER_PAY_LATER;
    else if (isBinderPayNowUser) return USER_TYPE_STRING.BINDER_PAY_NOW;
    else if (isMobileIFMActiveUser) return USER_TYPE_STRING.NMA_IFM_MEMBER;
    else if (isGuestGovernmentUser)
      return getUserTypeStringForDerivedLob(USER_TYPE_STRING.GOVT_GUEST);
    else if (isOneTimeGovernmentActiveUser)
      return getUserTypeStringForDerivedLob(USER_TYPE_STRING.GOVT_ACTIVE);
    else if (isBAMActiveGovernmentUser)
      return getUserTypeStringForDerivedLob(USER_TYPE_STRING.GOVT_BAM);
    else if (isMobileGovermentActiveUser)
      return getUserTypeStringForDerivedLob(USER_TYPE_STRING.NMA_GOVT);
    else if (isBrokerUser) return USER_TYPE_STRING.BROKER;
    else return '';
  };

  const isAccessTokenValid =
    accessTokenValid ||
    ((isActiveUser || isOneTimePaymentActiveUser || isActiveGovernmentUser) &&
      accessTokenDecoded &&
      accessTokenData.id_token &&
      accessTokenDecoded.scope.includes('openid') &&
      accessTokenDecoded.scope.includes('profile')) ||
    useMock;

  const isCaBinderPayLaterUser =
    isCA && origin === ORIGIN_TYPES.CA_BINDER_PAY_LATER;
  const isBinderUser = userType === USER_TYPES.BINDER_MEMBER;
  const isBinderPayNowUser =
    isBinderUser && origin === ORIGIN_TYPES.BINDER_PAY_NOW;
  const isBinderPayLaterUser =
    isBinderUser && origin === ORIGIN_TYPES.BINDER_PAY_LATER;

  const getCustomerServiceNumberForGovernment = ({
    dataSourceParam,
    derivedLobParam,
    stateParam,
  }: {
    dataSourceParam?: MEMBER_DATA_SOURCE;
    derivedLobParam?: MEMBER_DERIVED_LOB;
    stateParam?: MEMBER_STATE_CODE;
  }) => {
    const memberDataSource = dataSourceParam ? dataSourceParam : dataSource;
    const memberDerivedLOB = derivedLobParam ? derivedLobParam : derivedLob;
    const memberStateParam = stateParam
      ? stateParam
      : (state as MEMBER_STATE_CODE);
    if (memberDataSource && memberDerivedLOB && memberStateParam) {
      return CUSTOMER_SERVICE_NUMBER_CONFIGURATION[memberDataSource][
        memberDerivedLOB
      ][memberStateParam];
    }
    return '';
  };

  const getStateCodeCustomerServiceNumberByUserType = (
    stateCodeParam?: string,
    isErrorScreen?: boolean,
  ) => {
    let stateCodeByUserType = '';
    if (stateCodeParam) {
      stateCodeByUserType = stateCodeParam;
    } else if (
      (isOneTimePaymentActiveUser ||
        isBAMOrPublicSiteActiveUser ||
        isGuestUser ||
        isBrokerUser) &&
      memberDetails.memberBillingInfoRequestPayload?.state
    ) {
      stateCodeByUserType =
        memberDetails.memberBillingInfoRequestPayload?.state;
    } else if (
      isBinderPayNowUser &&
      binderPayNowLoginDetails?.memberStateCode
    ) {
      stateCodeByUserType = binderPayNowLoginDetails?.memberStateCode;
    } else if (
      isBinderPayLaterUser &&
      applicantBinderDetails?.applicantValidationList[0].state
    ) {
      stateCodeByUserType =
        applicantBinderDetails?.applicantValidationList[0].state;
    } else if (isGovernmentUser) {
      return getCustomerServiceNumberForGovernment({});
    } else if (isErrorScreen) {
      return '';
    } else {
      stateCodeByUserType = getStateCodeFromParsedUrl();
    }
    return stateCodeByUserType
      ? CUSTOMER_SERVICE_NUMBER[stateCodeByUserType]
      : '';
  };

  const getStateCodeProducerServiceNumberByUserType = (
    stateCodeParam?: string,
  ) => {
    let stateCodeByUserType = '';
    if (stateCodeParam) {
      stateCodeByUserType = stateCodeParam;
    } else if (
      isBrokerUser &&
      memberDetails.memberBillingInfoRequestPayload?.state
    ) {
      stateCodeByUserType =
        memberDetails.memberBillingInfoRequestPayload?.state;
    }
    return stateCodeByUserType
      ? PRODUCER_SERVICE_NUMBER[stateCodeByUserType]
      : '';
  };

  return {
    isBAMOrPublicSiteActiveUser,
    userType,
    setUserType,
    isOneTimePaymentActiveUser,
    isActiveUser,
    isAccessTokenValid,
    origin,
    isBinderUser,
    isBinderPayNowUser,
    isBinderPayLaterUser,
    isCA,
    isCAGovtMember,
    isCATermedMember,
    isGuestUser,
    isCaBinderPayLaterUser,
    getUserTypeString,
    isBAMUser,
    isGovernmentUser,
    isActiveGovernmentUser,
    isBAMActiveGovernmentUser,
    isOneTimeGovernmentActiveUser,
    isGuestGovernmentUser,
    getStateCodeCustomerServiceNumberByUserType,
    isGovernmentUser_WithMedsup,
    getCustomerServiceNumberForGovernment,
    isGovernmentUser_WithPDP,
    isGovernmentUser_WithMAPD,
    isActiveGovernmentTMGMedsupp,
    isActiveGovernmentMedsuppLegacy,
    isActiveGovernmentMAPDOrPDP,
    isMobileBAMUser,
    isActiveGovernmentTMGMember,
    isMobileGovermentActiveUser,
    isBrokerUser,
    getStateCodeProducerServiceNumberByUserType,
  };
};

export default useUserType;
